import {
  OperationVariables,
  QueryHookOptions,
  useLazyQuery,
} from "@apollo/client";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useEffect, useState } from "react";

interface CacheOrQueryOptions extends QueryHookOptions {
  /**
   * Time-to-live in milliseconds. Defaults to 2 minutes.
   */
  ttl: number;
}

/**
 * A reusable hook that caches GraphQL query results in localStorage with TTL.
 *
 * @param query - The GraphQL query TypedDocumentNode
 * @param options - Apollo query options (variables, etc.) and TTL
 * @param queryName - Unique name to identify this query in localStorage
 * @param skip - Whether to skip executing the query (default: false)
 * @returns Query result with data, loading, error, and refetch function
 *
 * @example
 * const { data, loading, error, refetch } = useCacheOrQuery(
 *   GET_USER_BALANCE,
 *   { ttl: 2 * 60 * 1000 },
 *   "userBalance",
 *   !isCreator
 * );
 */
function useCacheOrQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  query: TypedDocumentNode<TData, TVariables>,
  options: CacheOrQueryOptions,
  queryName: string,
  skip: boolean = false
) {
  const { ttl, ...apolloOptions } = options;

  const dataKey = `${queryName}_data`;
  const timestampKey = `${queryName}_timestamp`;

  const [hasCalled, setHasCalled] = useState(false);
  const [currentData, setCurrentData] = useState<TData | undefined>(undefined);

  const [executeQuery, queryResponse] = useLazyQuery<TData, TVariables>(query, {
    ...(apolloOptions as any),
    fetchPolicy: "network-only", // Always get fresh data from server when we do fetch
  });

  // Check if cache is valid
  const isCacheValid = (): boolean => {
    const cachedData = localStorage.getItem(dataKey);
    const cachedTimestamp = localStorage.getItem(timestampKey);

    if (!cachedData || !cachedTimestamp) {
      return false;
    }

    const timestamp = parseInt(cachedTimestamp, 10);
    const now = Date.now();
    const timeSinceCache = now - timestamp;

    return timeSinceCache < ttl;
  };

  // Get cached data
  const getCachedData = (): TData | null => {
    try {
      const cachedData = localStorage.getItem(dataKey);
      if (!cachedData) return null;
      return JSON.parse(cachedData) as TData;
    } catch (error) {
      console.error(`Error parsing cached data for ${queryName}:`, error);
      return null;
    }
  };

  // Save to cache
  const saveToCache = (data: TData) => {
    try {
      localStorage.setItem(dataKey, JSON.stringify(data));
      localStorage.setItem(timestampKey, Date.now().toString());
    } catch (error) {
      console.error(`Error saving to cache for ${queryName}:`, error);
    }
  };

  // Clear cache
  const clearCache = () => {
    localStorage.removeItem(dataKey);
    localStorage.removeItem(timestampKey);
  };

  // Manual refetch that bypasses cache
  const refetch = async () => {
    setHasCalled(true);
    const result = await executeQuery();
    if (result.data) {
      saveToCache(result.data);
      setCurrentData(result.data);
    }
    return result;
  };

  // Initialize currentData from cache if valid and not yet set
  useEffect(() => {
    if (!skip && currentData === undefined && isCacheValid()) {
      const cachedData = getCachedData();
      if (cachedData) {
        setCurrentData(cachedData);
      }
    }
  }, [skip]);

  // Execute query if not cached and not already called
  useEffect(() => {
    if (!hasCalled && !skip && !isCacheValid()) {
      setHasCalled(true);
      executeQuery();
    }
  }, [hasCalled, skip]);

  // Save query result to cache when it completes and update current data
  useEffect(() => {
    if (!skip && !queryResponse.loading && queryResponse.data) {
      saveToCache(queryResponse.data);
      setCurrentData(queryResponse.data);
    }
  }, [skip, queryResponse.loading, queryResponse.data]);

  // Skip execution if requested
  if (skip) {
    return {
      data: undefined,
      loading: false,
      error: undefined,
      refetch,
      clearCache,
    };
  }

  // Return current data state
  return {
    data: currentData ?? queryResponse.data,
    loading: queryResponse.loading,
    error: queryResponse.error,
    refetch,
    clearCache,
  };
}

export default useCacheOrQuery;
